import { useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useZero } from '@rocicorp/zero/react'
import { useAuth } from '../auth/AuthContext.tsx'
import { MemberForm } from '../components/MemberForm.tsx'
import { Shell } from '../components/Shell.tsx'
import { formatDate, formatTime } from '../lib/format.ts'
import { mutators } from '../zero/mutators.ts'
import { queries } from '../zero/queries.ts'

import { Require } from './guards.tsx'

function MembersInner() {
  const z = useZero()
  const navigate = useNavigate()
  const { session } = useAuth()
  const [members] = useQuery(queries.allMembers())
  const [privateRows] = useQuery(queries.memberPrivateRoster())
  const [pins] = useQuery(queries.assignedPins())
  const [editingId, setEditingId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [message, setMessage] = useState('')

  const privateById = useMemo(
    () => new Map(privateRows.map((row) => [row.userId, row])),
    [privateRows],
  )
  const pinByUser = useMemo(
    () => new Map(pins.flatMap((row) => (row.userId ? [[row.userId, row] as const] : []))),
    [pins],
  )
  // Every assigned PIN, so a generated one never collides with a live member or
  // a deleted member's reserved value. The kiosk's row is never synced.
  const takenPins = useMemo(() => new Set(pins.map((row) => row.pinCode)), [pins])

  const active = members.filter((member) => member.deletedAt == null)
  const deleted = members.filter((member) => member.deletedAt != null)

  async function removeMember(userId: string, name: string) {
    if (!window.confirm(`Delete ${name}? Their drinks, fees and payments are kept.`)) return
    setMessage('')
    try {
      const mutation = z.mutate(mutators.members.remove({ userId }))
      const clientResult = await mutation.client
      if (clientResult.type === 'error') {
        setMessage(clientResult.error.message)
        return
      }
      setMessage('Member deleted · syncing…')
      void mutation.server.then((serverResult) => {
        setMessage(
          serverResult.type === 'success' ? 'Member deleted' : serverResult.error.message,
        )
      })
    } catch {
      setMessage('Failed to delete member')
    }
  }

  return (
    <Shell title="Members" wide onBack={() => navigate({ to: '/admin' })}>
      {!creating && editingId == null ? (
        <div className="admin-actions">
          <button className="btn btn-sm" onClick={() => setCreating(true)}>
            Add member
          </button>
        </div>
      ) : null}

      {creating ? (
        <MemberForm takenPins={takenPins} onClose={() => setCreating(false)} />
      ) : null}

      <div className="section-title">Active members ({active.length})</div>
      <div className="card table-wrap">
        {active.length === 0 ? (
          <p className="empty">No active members.</p>
        ) : (
          <table className="member-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>PIN</th>
                <th>Admin</th>
                <th>Approved</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {active.map((member) => (
                <tr key={member.id}>
                  <td>
                    <Link
                      className="text-link"
                      to="/users/$userId"
                      params={{ userId: member.id }}
                    >
                      {member.displayName}
                    </Link>
                    {member.numberOnBack != null ? (
                      <small>
                        {member.nameOnBack ?? ''} #{member.numberOnBack}
                      </small>
                    ) : null}
                  </td>
                  <td>{privateById.get(member.id)?.email ?? <span className="muted">—</span>}</td>
                  <td className="pin-cell">{pinByUser.get(member.id)?.pinCode ?? '—'}</td>
                  <td>{member.isAdmin ? 'Yes' : 'No'}</td>
                  <td>{member.isApproved ? 'Yes' : 'No'}</td>
                  <td>{formatTime(member.createdAt)}</td>
                  <td className="row-actions">
                    <button className="btn-ghost" onClick={() => setEditingId(member.id)}>
                      Edit
                    </button>
                    {member.id !== session?.auth.sub ? (
                      <button
                        className="btn-ghost"
                        onClick={() => void removeMember(member.id, member.displayName)}
                        title="Delete member"
                      >
                        ✕
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {message ? <p className="form-message">{message}</p> : null}
      </div>

      {editingId != null
        ? (() => {
            const member = active.find((candidate) => candidate.id === editingId)
            if (!member) return null
            return (
              <MemberForm
                key={member.id}
                member={member}
                privateRow={privateById.get(member.id)}
                pin={pinByUser.get(member.id)}
                takenPins={takenPins}
                onClose={() => setEditingId(null)}
              />
            )
          })()
        : null}

      <details className="deleted-members">
        <summary>Deleted members ({deleted.length})</summary>
        <div className="card">
          {deleted.length === 0 ? (
            <p className="empty">None.</p>
          ) : (
            deleted.map((member) => (
              <div className="line" key={member.id}>
                <div className="line-main">
                  <div className="line-title">{member.displayName}</div>
                  <div className="line-sub">
                    Deleted {formatDate(member.deletedAt)} · jersey freed, PIN still reserved
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </details>
    </Shell>
  )
}

export function AdminMembersPage() {
  return (
    <Require roles={['admin']} showDenied>
      <MembersInner />
    </Require>
  )
}
